import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { ArrowLeft, Loader2, Eye, EyeOff, Lock, CheckCircle, AlertCircle, Shield, Image as ImageIcon } from 'lucide-react';

type Step = 'loading' | 'password' | 'totp' | 'success';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoLoading, setLogoLoading] = useState(true);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [tokenError, setTokenError] = useState(false);
  const [step, setStep] = useState<Step>('loading');
  const [totpCode, setTotpCode] = useState('');
  const [totpError, setTotpError] = useState('');
  const [isVerifyingTotp, setIsVerifyingTotp] = useState(false);
  const [sessionToken, setSessionToken] = useState('');

  useEffect(() => {
    fetchLogo();
    handleRecoveryToken();
  }, []);

  const handleRecoveryToken = async () => {
    const hash = window.location.hash;
    const params = new URLSearchParams(hash.replace('#', ''));
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token') || '';
    const type = params.get('type');

    if (!accessToken || type !== 'recovery') {
      setTokenError(true);
      setStep('password');
      return;
    }

    try {
      const { data, error } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });

      if (error || !data.session) {
        setTokenError(true);
        setStep('password');
        return;
      }

      setSessionToken(data.session.access_token);
      setStep('password');
    } catch {
      setTokenError(true);
      setStep('password');
    }
  };

  const fetchLogo = async () => {
    try {
      setLogoLoading(true);
      const defaultLogo = 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=200&h=80&fit=crop&auto=format';
      setLogoUrl(defaultLogo);

      const { data, error } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'logo_url')
        .maybeSingle();

      if (!error && data?.value) {
        const img = new Image();
        img.onload = () => { setLogoUrl(data.value); setLogoLoading(false); };
        img.onerror = () => setLogoLoading(false);
        img.src = data.value;
      } else {
        setLogoLoading(false);
      }
    } catch {
      setLogoLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setStep('totp');
  };

  const handleTotpVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setTotpError('');

    if (totpCode.length !== 6) {
      setTotpError('Please enter a 6-digit code.');
      return;
    }

    setIsVerifyingTotp(true);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token || sessionToken;

      if (!token) {
        setTotpError('Session expired. Please request a new reset link.');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/twofa-verify`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ token: totpCode, enable: false }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        setTotpError(data.error || 'Invalid authentication code. Please try again.');
        return;
      }

      await performPasswordReset();
    } catch {
      setTotpError('Failed to verify code. Please try again.');
    } finally {
      setIsVerifyingTotp(false);
    }
  };

  const performPasswordReset = async () => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      await supabase.auth.signOut();
      setStep('success');
      setTimeout(() => navigate('/consultant/login'), 3000);
    } catch (err: any) {
      setTotpError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTotpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 6) {
      setTotpCode(value);
      setTotpError('');
    }
  };

  if (step === 'loading') {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
      </div>
    );
  }

  if (tokenError) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-4">Invalid Reset Link</h1>
          <p className="text-gray-400 mb-6">
            This password reset link is invalid or has expired. Please request a new one.
          </p>
          <button
            onClick={() => navigate('/consultant/login')}
            className="bg-primary text-black font-semibold px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">
      <nav className="fixed w-full z-50 bg-black backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <button
              onClick={() => navigate('/')}
              className="text-white hover:text-primary transition-colors -mt-4 -mb-4"
            >
              {logoLoading ? (
                <div className="h-32 md:h-28 w-[200px] flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
              ) : logoUrl ? (
                <img src="/1.png" alt="Logo" className="h-40 w-auto object-contain" />
              ) : (
                <ImageIcon size={32} className="text-primary" />
              )}
            </button>
          </div>
        </div>
      </nav>

      <div className="flex-1 flex items-center justify-center px-4 pt-32 pb-12">
        <div className="max-w-md w-full">
          {step !== 'success' && (
            <button
              onClick={() => step === 'totp' ? setStep('password') : navigate('/consultant/login')}
              className="flex items-center text-gray-400 hover:text-white transition-colors mb-8"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              {step === 'totp' ? 'Back to Password' : 'Back to Login'}
            </button>
          )}

          <div className="bg-gray-800 rounded-xl p-8 border border-gray-700">

            {step === 'success' && (
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500/20 rounded-full mb-6">
                  <CheckCircle className="w-10 h-10 text-green-400" />
                </div>
                <h2 className="text-2xl font-bold mb-3">Password Reset Successful</h2>
                <p className="text-gray-400 mb-2">Your password has been successfully reset.</p>
                <p className="text-sm text-gray-500">Redirecting to login...</p>
              </div>
            )}

            {step === 'password' && (
              <>
                <h2 className="text-2xl font-bold mb-1">Reset Your Password</h2>
                <p className="text-gray-400 mb-6 text-sm">Enter your new password below. You will need to verify with your authenticator app next.</p>

                {error && (
                  <div className="mb-5 p-4 bg-red-500/10 border border-red-500/50 rounded-lg flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-400">{error}</p>
                  </div>
                )}

                <form onSubmit={handlePasswordSubmit} className="space-y-5">
                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium mb-2">New Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="newPassword"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full pl-10 pr-12 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-white"
                        placeholder="Enter new password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Must be at least 8 characters</p>
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium mb-2">Confirm New Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full pl-10 pr-12 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-white"
                        placeholder="Confirm new password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                      >
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-primary text-black font-bold py-3 rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center"
                  >
                    Verify Account
                  </button>
                </form>
              </>
            )}

            {step === 'totp' && (
              <>
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500/20 rounded-full mb-4">
                    <Shield className="w-8 h-8 text-blue-400" />
                  </div>
                  <h2 className="text-2xl font-bold mb-2">Verify Your Identity</h2>
                  <p className="text-gray-400 text-sm">Enter the 6-digit code from your authenticator app to complete the password reset.</p>
                </div>

                {totpError && (
                  <div className="mb-5 p-4 bg-red-500/10 border border-red-500/50 rounded-lg flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-400">{totpError}</p>
                  </div>
                )}

                <form onSubmit={handleTotpVerify} className="space-y-5">
                  <div>
                    <label htmlFor="totpCode" className="block text-sm font-medium mb-2 text-center">Authentication Code</label>
                    <input
                      type="text"
                      id="totpCode"
                      value={totpCode}
                      onChange={handleTotpChange}
                      placeholder="000000"
                      className="w-full px-4 py-4 border border-gray-600 rounded-lg text-center text-3xl tracking-widest font-mono focus:ring-2 focus:ring-primary focus:border-transparent bg-gray-700 text-white placeholder-gray-500"
                      maxLength={6}
                      autoComplete="off"
                      autoFocus
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isVerifyingTotp || isSubmitting || totpCode.length !== 6}
                    className="w-full bg-primary text-black font-bold py-3 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isVerifyingTotp || isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        {isSubmitting ? 'Resetting Password...' : 'Verifying...'}
                      </>
                    ) : (
                      'Verify & Reset Password'
                    )}
                  </button>
                </form>

                <div className="mt-6 pt-5 border-t border-gray-700">
                  <p className="text-sm text-gray-500 text-center">
                    Lost access to your authenticator app?{' '}
                    <a href="/request-access" className="text-blue-400 hover:underline font-medium">Contact support</a>
                  </p>
                </div>
              </>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
